/**
 * Types for evidence documents linked to a FIM subject (a claim, and — as the
 * evidence panels get reused — other dossier subjects such as equipment or
 * instruments). Moved out of `pages/claims/types.ts` so the shared evidence
 * panels in this feature can be consumed from multiple pages. The shapes are
 * unchanged from the original claim types; `pages/claims/types.ts` re-exports
 * them under their former names as deprecated aliases.
 */
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

/**
 * Parse fim `Reference.boxes` strings (`"bbox:x0,y0,x1,y1"`, normalized page
 * fractions) into the viewer's rect shape. Malformed entries are dropped —
 * a citation with no parseable geometry degrades to a plain page open.
 */
export const parseBboxStrings = (
  boxes: string[] | null | undefined
): NormalizedBBox[] => {
  const out: NormalizedBBox[] = []
  for (const raw of boxes ?? []) {
    const nums = raw
      .replace(/^bbox:/i, '')
      .split(',')
      .map((s) => Number(s.trim()))
    if (nums.length !== 4 || nums.some((n) => !Number.isFinite(n))) continue
    const [x0, y0, x1, y1] = nums
    out.push({ left: x0, top: y0, width: x1 - x0, height: y1 - y0 })
  }
  return out
}

/**
 * How a document relates to the subject, assigned per-document by the dossier
 * pipeline. `establishes`/`supporting_hardware` are affirmative supporting
 * references; `excluded` is negative evidence (searched, confirmed absent);
 * `device_manual` entries are vendor/GE manuals identified by document number
 * (no openable `DOC-` id); `other` is context.
 */
export type EvidenceDocRelation =
  | 'establishes'
  | 'supporting_hardware'
  | 'device_manual'
  | 'excluded'
  | 'other'

export interface EvidenceLinkedDocument {
  doc_id?: string
  /** Document number for manual/spec references that have no `DOC-` id. */
  document_number?: string
  title?: string
  role?: string
  evidence?: string
  drawing?: string
  page?: number
  evidence_tier?: string
  forecast_url?: string
  relation?: EvidenceDocRelation
  relation_why?: string
  /**
   * Whether the verification gate confirmed the stated `relation`. When `false`
   * for an affirmative relation, the reference is unverified and must not be
   * auto-opened or shown as confirming the tag.
   */
  relation_verified?: boolean
  /**
   * Cited region(s) on `page`, as normalized page-fraction rects. When present
   * the viewer opens the document already framed on (and highlighting) the
   * region instead of showing the whole page.
   */
  boxes?: NormalizedBBox[]
}
